import {
  Component,
  OnInit,
  OnDestroy,
  inject,
  ChangeDetectionStrategy,
  WritableSignal,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { Subscription, Observable, of } from 'rxjs';
import { catchError, first } from 'rxjs/operators';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { NgClass, NgTemplateOutlet, AsyncPipe } from '@angular/common';
import { AuthService } from '../../../services/auth/auth.service';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { APP_NAME, Roles, SHOW_ETHNIKI_LOGO } from '../../../utils';
import { TranslationService } from '../../../services/translation.service';
import { TranslatePipe } from '@ngx-translate/core';
import { User } from '../../../interfaces';
import { AppConfigService } from '../../../services/app-config.service';
import { EmailMfaComponent } from '../email-mfa/email-mfa.component';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NgClass,
    NgTemplateOutlet,
    RouterLink,
    AsyncPipe,
    InputTextModule,
    ButtonModule,
    TranslatePipe,
    EmailMfaComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private authService = inject(AuthService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  translateService = inject(TranslationService);
  APP_NAME = inject(APP_NAME);
  showEthnikiLogo = inject(SHOW_ETHNIKI_LOGO);

  // KeenThemes mock, change it to:
  defaultAuth: any = {};

  loginForm!: FormGroup;
  hasError: WritableSignal<boolean> = signal(false);
  hasMfaError: WritableSignal<boolean> = signal(false);
  isMfaStep: WritableSignal<boolean> = signal(false);
  returnUrl!: string;
  isLoading$: Observable<boolean>;
  private unsubscribe: Subscription[] = [];
  private pendingCredentials?: { email: string; password: string };
  mfaEmail = '';

  loginLogo = '';

  private config = inject(AppConfigService);

  constructor() {
    this.isLoading$ = this.authService.isLoading$;
    // redirect to home if already logged in
    if (this.authService.currentUserValue) {
      this.router.navigate(['/']);
    }
  }

  ngOnInit(): void {
    this.initForm();
    this.returnUrl = this.getReturnUrl(this.route.snapshot.queryParams['returnUrl']) ?? '/';
  }

  // convenience getter for easy access to form fields
  get f() {
    return this.loginForm.controls;
  }

  initForm() {
    this.loginForm = this.fb.group({
      email: [
        this.defaultAuth.email,
        Validators.compose([
          Validators.required,
          Validators.email,
          Validators.minLength(3),
          Validators.maxLength(320),
        ]),
      ],
      password: [
        this.defaultAuth.password,
        Validators.compose([
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(100),
        ]),
      ],
    });
  }

  submit() {
    this.hasError.set(false);
    this.hasMfaError.set(false);
    const email = this.f['email'].value;
    const password = this.f['password'].value;
    this.pendingCredentials = { email, password };

    const loginSubscr = this.authService
      .login(email, password)
      .pipe(
        first(),
        catchError(() => {
          this.hasError.set(true);
          this.pendingCredentials = undefined;

          return of(undefined);
        })
      )
      .subscribe({
        next: (challengeOrUser: any) => {
          if (challengeOrUser?.requiresMfa) {
            this.mfaEmail = challengeOrUser.email || email;
            this.isMfaStep.set(true);
          } else if (challengeOrUser && 'role' in challengeOrUser) {
            this.navigateAfterLogin(challengeOrUser as User);
          }
        },
      });
    this.unsubscribe.push(loginSubscr);
  }

  verifyMfa(code: string): void {
    this.hasMfaError.set(false);

    const verifySubscr = this.authService
      .verifyMfa(this.mfaEmail, code)
      .pipe(
        first(),
        catchError(() => {
          this.hasMfaError.set(true);

          return of(undefined);
        })
      )
      .subscribe({
        next: (user: User | undefined) => {
          if (user) {
            this.navigateAfterLogin(user);
          }
        },
      });

    this.unsubscribe.push(verifySubscr);
  }

  resendMfaCode(): void {
    if (!this.pendingCredentials) {
      this.backToLogin();
      return;
    }

    this.hasMfaError.set(false);
    const loginSubscr = this.authService
      .login(this.pendingCredentials.email, this.pendingCredentials.password)
      .pipe(
        first(),
        catchError(() => {
          this.hasMfaError.set(true);

          return of(undefined);
        })
      )
      .subscribe({
        next: (challengeOrUser: any) => {
          if (challengeOrUser?.requiresMfa) {
            this.mfaEmail = challengeOrUser.email || this.pendingCredentials!.email;
          } else if (challengeOrUser && 'role' in challengeOrUser) {
            this.navigateAfterLogin(challengeOrUser as User);
          }
        },
      });

    this.unsubscribe.push(loginSubscr);
  }

  backToLogin(): void {
    this.isMfaStep.set(false);
    this.hasMfaError.set(false);
    this.pendingCredentials = undefined;
  }

  ngOnDestroy() {
    this.unsubscribe.forEach((sb) => sb.unsubscribe());
  }

  private getReturnUrl(returnUrl: unknown): string | null {
    if (typeof returnUrl !== 'string') {
      return null;
    }

    // Allow only absolute in-app paths to avoid open redirects.
    if (!returnUrl.startsWith('/') || returnUrl.startsWith('//') || returnUrl.includes('://')) {
      return null;
    }

    if (returnUrl.startsWith('/auth')) {
      return null;
    }

    return returnUrl;
  }

  private navigateAfterLogin(user: User): void {
    const rolesWithDashboard = [Roles.Administrator, Roles.CaseManager, Roles.Guest];
    const isInvestigatorPortal = this.APP_NAME === 'investigator-portal';
    const hasDashboardAccess =
      rolesWithDashboard.includes(user.role as Roles) ||
      (isInvestigatorPortal && user.role === Roles.Investigator);
    const defaultPath = hasDashboardAccess ? '/' : '/case-table';
    const requestedPath = this.returnUrl;

    const shouldUseRequestedPath = !!requestedPath && (hasDashboardAccess || (requestedPath !== '/' && requestedPath !== '/dashboard'));
    const targetPath = shouldUseRequestedPath ? requestedPath : defaultPath;

    this.router.navigateByUrl(targetPath);
  }
}
